/**
 * Utility to construct dynamic Order Now URLs based on session data
 */

const QR_SCAN_BASE_URL = "https://cbtl.fudr.in/app/scan";

/**
 * Checks if a URL is an order URL that should be dynamically constructed
 */
export const isOrderUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return (
    url.includes('cbtl.uengage.in/order') || 
    url.includes('beanbot.mindrops.com/the-coffee-bean-tea-leaf') ||
    url.includes('beanbot.mindrops.com/order')
  );
};

/**
 * Extracts QR/table code from chatbot session data
 */
const extractQrCodeFromSession = (sessionData) => {
  if (!sessionData || typeof sessionData !== "object") {
    return null;
  }

  // Support multiple possible keys from chatbot/session payloads.
  const rawCode =
    sessionData.qrCode ||
    sessionData.tableCode ||
    sessionData.tableNumber ||
    sessionData.code ||
    null;

  if (!rawCode || typeof rawCode !== "string") {
    return null;
  }

  const normalizedCode = rawCode.trim().toUpperCase();
  // Existing table numbers are numeric; scan codes are alphanumeric.
  // This keeps old ?t=11216 flow intact and enables /1X3CV7 style flow.
  if (/^\d+$/.test(normalizedCode)) {
    return null;
  }
  return normalizedCode || null;
};

/**
 * Constructs scan order URL from chatbot QR/table code
 */
const buildScanOrderUrl = (sessionData) => {
  const qrCode = extractQrCodeFromSession(sessionData);
  if (!qrCode) return null;
  return `${QR_SCAN_BASE_URL}/${qrCode}`;
};

/**
 * Constructs the Order Now URL using session data
 * Format: https://cbtl.uengage.in/order/the-coffee-bean-tea-leaf-{{location}}-{{city}}?t={{t}}
 */
export const buildOrderUrl = (sessionData) => {
  if (!sessionData) return null;
  
  const { location, city, tableNumber } = sessionData;
  
  // If any required parameter is missing, return null
  if (!location || !city || !tableNumber) {
    return null;
  }
  
  // Construct the URL
  const baseUrl = `https://cbtl.uengage.in/order/the-coffee-bean-tea-leaf-${location}-${city}`;
  const urlWithParams = `${baseUrl}?t=${tableNumber}`;
  
  return urlWithParams;
};

/**
 * Replaces a static order URL with a dynamic one based on session data
 * If the URL is an order URL and session data is available, returns the dynamic URL
 * Otherwise, returns the original URL
 */
export const replaceOrderUrl = (url, sessionData) => {
  if (!isOrderUrl(url)) {
    return url;
  }

  // Priority 1: QR/table code received in chatbot session data.
  const scanOrderUrl = buildScanOrderUrl(sessionData);
  if (scanOrderUrl) {
    return scanOrderUrl;
  }

  // Priority 2: Existing location/city/tableNumber query-parameter flow
  const dynamicUrl = buildOrderUrl(sessionData);
  return dynamicUrl || url; // Fallback to original URL if dynamic construction fails
};
